import React, { useState } from "react";
import { mockData } from "../data/mockData";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { ArrowRight, TrendingUp } from "lucide-react";

const WorkSection = ({ data }) => {
  const impactStories = data?.impactStories || [];
  const testimonials = data?.testimonials || [];
  const [selectedStory, setSelectedStory] = useState(null);

  return (
    <section id="work" className="py-24 bg-gray-900">
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          
          {/* Section Header */}
          <div className="text-center mb-20">
            <p className="text-sm text-red-500 mb-4 tracking-wider uppercase">OUR IMPACT</p>
            <h2 className="text-4xl md:text-5xl font-light mb-8">Transforming Lives</h2>
            <div className="w-24 h-px bg-red-500 mx-auto mb-8"></div>
            <p className="text-xl text-white/80 max-w-3xl mx-auto font-light">
              Real stories of how our specialised healthcare services are making a difference 
              in retirement villages across NSW
            </p>
          </div>

          {/* Community Engagement Photo */}
          <div className="mb-20">
            <div className="relative rounded-lg overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_senior-living-plus/artifacts/68v0v2oz_AWB%20Photo.jpg"
                alt="Reignite Health team member engaging with retirement village residents in a community wellness session"
                className="w-full h-96 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/40 to-black/80"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center max-w-4xl px-8">
                  <h3 className="text-3xl md:text-4xl font-light text-white mb-6">
                    Real <span className="text-orange-500">Community</span>, Real <span className="text-red-500">Results</span>
                  </h3>
                  <p className="text-lg text-white/90 leading-relaxed font-light">
                    This is what makes us different - we don't just provide services, we become part of your community. 
                    Every session, every program, every interaction is built on genuine relationships and shared commitment to wellbeing.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Impact Stories Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
            {impactStories.map((story, index) => (
              <Card
                key={story.id}
                className={`bg-black/40 border-white/10 hover:border-red-500/30 transition-all duration-500 cursor-pointer group ${
                  selectedStory === story.id ? 'border-red-500/50 scale-105' : ''
                }`}
                onClick={() => setSelectedStory(selectedStory === story.id ? null : story.id)}
              >
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <Badge variant="outline" className="border-red-500/30 text-red-500/80">
                      {story.category}
                    </Badge>
                    <div className="flex items-center text-green-400">
                      <TrendingUp className="w-4 h-4 mr-1" />
                      <span className="text-sm font-light">{story.impact}</span>
                    </div>
                  </div>
                  <CardTitle className="text-xl font-light text-white group-hover:text-red-100 transition-colors duration-300">
                    {story.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-white/70 leading-relaxed font-light mb-4">
                    {story.description}
                  </p>
                  <div className="flex items-center text-red-500 group-hover:text-red-400 transition-colors duration-300">
                    <span className="text-sm font-light mr-2">Learn more</span>
                    <ArrowRight className={`w-4 h-4 transition-transform duration-300 ${
                      selectedStory === story.id ? 'translate-x-1' : ''
                    }`} />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Testimonials */}
          <div className="mb-16">
            <h3 className="text-3xl font-light text-center mb-12">Resident Reviews</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {testimonials.slice(0, 3).map((testimonial) => (
                <Card 
                  key={testimonial.id}
                  className="bg-gray-800/50 border-white/10 hover:border-red-500/30 transition-all duration-300 hover:scale-105 group"
                >
                  <CardContent className="p-8">
                    <div className="mb-6">
                      <div className="flex text-red-500 mb-4">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <span key={i} className="text-lg">★</span>
                        ))}
                      </div>
                      <p className="text-white/80 italic leading-relaxed font-light mb-6">
                        "{testimonial.quote}"
                      </p>
                    </div>
                    <div className="border-t border-white/10 pt-4">
                      <p className="text-white font-light">{testimonial.name}{testimonial.age ? `, ${testimonial.age}` : ''}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="text-center">
            <h3 className="text-2xl md:text-3xl font-light mb-6">
              Ready to transform your retirement village's healthcare?
            </h3>
            <p className="text-white/80 mb-8 font-light">
              Join the growing community of villages that have chosen Reignite Health 
              for their residents' wellbeing.
            </p>
            <button 
              className="bg-red-500 hover:bg-red-600 text-white px-8 py-3 rounded-sm font-light transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-red-500/25"
              onClick={() => {
                const element = document.querySelector("#contact");
                if (element) element.scrollIntoView({ behavior: "smooth" });
              }}
            >
              Partner With Us
              <ArrowRight className="ml-2 w-4 h-4 inline" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WorkSection;